<script setup>
import { ref, computed, watch } from 'vue';
import FeedbackReportsApi from 'dashboard/api/inbox/feedbackReports';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  show: { type: Boolean, required: true },
  conversationId: { type: [String, Number], required: true },
  // Optional — when the agent was looking at an AI draft we link it so the
  // super admin can see the suggested content alongside the feedback.
  aiDraftId: { type: [String, Number], default: null },
});

const emit = defineEmits(['close', 'submitted']);

const { t } = useI18n();

// Topics are defined server-side (AiDraftFeedback::TOPICS) — keep the two in
// sync when adding new topics.
const TOPICS = [
  { key: 'ai_suggestion', i18n: 'AI_SUGGESTION', icon: 'i-ph-sparkle' },
  { key: 'email_sync', i18n: 'EMAIL_SYNC', icon: 'i-ph-envelope' },
  { key: 'workflow', i18n: 'WORKFLOW', icon: 'i-ph-flow-arrow' },
  { key: 'other', i18n: 'OTHER', icon: 'i-ph-dots-three-outline' },
];

// Categories are topic-specific — keys mirror AiDraftFeedback::CATEGORIES_BY_TOPIC.
const CATEGORIES_BY_TOPIC = {
  ai_suggestion: [
    { key: 'tone', i18n: 'TONE' },
    { key: 'factual', i18n: 'FACTUAL' },
    { key: 'hallucination', i18n: 'HALLUCINATION' },
    { key: 'missing_info', i18n: 'MISSING_INFO' },
    { key: 'format', i18n: 'FORMAT' },
    { key: 'irrelevant', i18n: 'IRRELEVANT' },
    { key: 'kb_misuse', i18n: 'KB_MISUSE' },
    { key: 'other', i18n: 'OTHER' },
  ],
  email_sync: [
    { key: 'missing_emails', i18n: 'MISSING_EMAILS' },
    { key: 'duplicates', i18n: 'DUPLICATES' },
    { key: 'wrong_threading', i18n: 'WRONG_THREADING' },
    { key: 'delay', i18n: 'DELAY' },
    { key: 'attachment_lost', i18n: 'ATTACHMENT_LOST' },
    { key: 'other', i18n: 'OTHER' },
  ],
  workflow: [
    { key: 'assignment', i18n: 'ASSIGNMENT' },
    { key: 'routing', i18n: 'ROUTING' },
    { key: 'ui_confusing', i18n: 'UI_CONFUSING' },
    { key: 'permission', i18n: 'PERMISSION' },
    { key: 'notification', i18n: 'NOTIFICATION' },
    { key: 'other', i18n: 'OTHER' },
  ],
  other: [],
};

const RATINGS = [
  { value: 3, icon: 'i-ph-thumbs-up-fill', i18n: 'GOOD', tint: 'text-n-teal-11 bg-n-teal-3' },
  { value: 2, icon: 'i-ph-minus-circle-fill', i18n: 'NEUTRAL', tint: 'text-n-slate-11 bg-n-slate-3' },
  { value: 1, icon: 'i-ph-thumbs-down-fill', i18n: 'BAD', tint: 'text-n-ruby-11 bg-n-ruby-3' },
];

const topic = ref('ai_suggestion');
const rating = ref(null);
const selectedCategories = ref([]);
const comment = ref('');
const submitting = ref(false);

const currentCategories = computed(() => CATEGORIES_BY_TOPIC[topic.value] || []);

// If topic has no applicable ai_draft (e.g. workflow on a conversation without
// a draft) we still anchor to conversation_id server-side.
const effectiveDraftId = computed(() =>
  topic.value === 'ai_suggestion' ? props.aiDraftId : null
);

const canSubmit = computed(
  () =>
    rating.value !== null ||
    selectedCategories.value.length > 0 ||
    comment.value.trim().length > 0
);

// When topic switches, drop any categories from the previous topic so we never
// submit an invalid combo (the server validates, but this keeps UX clean).
watch(topic, () => {
  selectedCategories.value = [];
});

const toggleCategory = key => {
  const idx = selectedCategories.value.indexOf(key);
  if (idx >= 0) selectedCategories.value.splice(idx, 1);
  else selectedCategories.value.push(key);
};

const reset = () => {
  topic.value = 'ai_suggestion';
  rating.value = null;
  selectedCategories.value = [];
  comment.value = '';
};

const close = () => {
  if (submitting.value) return;
  reset();
  emit('close');
};

const submit = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;
  try {
    await FeedbackReportsApi.create(props.conversationId, {
      topic: topic.value,
      rating: rating.value,
      categories: selectedCategories.value,
      comment: comment.value.trim() || null,
      aiDraftId: effectiveDraftId.value,
    });
    useAlert(t('FEEDBACK_MODAL.SUBMITTED'));
    emit('submitted');
    reset();
    emit('close');
  } catch (e) {
    useAlert(e?.response?.data?.error || t('FEEDBACK_MODAL.FAILED'));
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="close"
  >
    <div
      class="bg-n-background dark:bg-n-slate-2 w-full max-w-2xl rounded-lg shadow-xl flex flex-col max-h-[90vh]"
    >
      <!-- Header -->
      <header class="flex items-start justify-between px-5 py-4 border-b border-n-slate-4">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ $t('FEEDBACK_MODAL.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ $t('FEEDBACK_MODAL.SUBTITLE') }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 p-1 -mr-1 -mt-1"
          :disabled="submitting"
          @click="close"
        >
          <span class="i-ph-x text-lg" />
        </button>
      </header>

      <!-- Body (scrollable) -->
      <div class="px-5 py-4 overflow-y-auto space-y-5">
        <!-- Topic -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('FEEDBACK_MODAL.TOPIC_LABEL') }}
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              v-for="tp in TOPICS"
              :key="tp.key"
              type="button"
              class="flex flex-col items-start gap-1 text-left py-2 px-3 rounded-md border transition"
              :class="
                topic === tp.key
                  ? 'bg-n-violet-3 border-n-violet-6 text-n-violet-11'
                  : 'border-n-slate-5 text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="topic = tp.key"
            >
              <span class="flex items-center gap-1.5 text-sm font-medium">
                <span :class="tp.icon" />
                {{ $t(`FEEDBACK_MODAL.TOPICS.${tp.i18n}`) }}
              </span>
              <span class="text-[11px] text-n-slate-11 leading-snug">
                {{ $t(`FEEDBACK_MODAL.TOPIC_HINTS.${tp.i18n}`) }}
              </span>
            </button>
          </div>
        </div>

        <!-- Rating -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('FEEDBACK_MODAL.RATING_LABEL') }}
          </label>
          <div class="flex gap-2">
            <button
              v-for="r in RATINGS"
              :key="r.value"
              type="button"
              class="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md border transition"
              :class="
                rating === r.value
                  ? `${r.tint} border-transparent ring-2 ring-offset-1 ring-n-slate-6`
                  : 'border-n-slate-5 text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="rating = rating === r.value ? null : r.value"
            >
              <span :class="r.icon" />
              <span class="text-xs">{{
                $t(`FEEDBACK_MODAL.RATINGS.${r.i18n}`)
              }}</span>
            </button>
          </div>
        </div>

        <!-- Categories (topic-specific) -->
        <div v-if="currentCategories.length > 0">
          <label class="block text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('FEEDBACK_MODAL.CATEGORIES_LABEL') }}
          </label>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="c in currentCategories"
              :key="c.key"
              type="button"
              class="text-xs px-2.5 py-1 rounded-full border transition"
              :class="
                selectedCategories.includes(c.key)
                  ? 'bg-n-violet-3 border-n-violet-6 text-n-violet-11'
                  : 'border-n-slate-5 text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="toggleCategory(c.key)"
            >
              {{ $t(`FEEDBACK_MODAL.CATEGORIES.${c.i18n}`) }}
            </button>
          </div>
        </div>

        <!-- Comment -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('FEEDBACK_MODAL.COMMENT_LABEL') }}
          </label>
          <textarea
            v-model="comment"
            rows="5"
            :placeholder="$t('FEEDBACK_MODAL.COMMENT_PLACEHOLDER')"
            class="w-full border border-n-slate-5 rounded-md px-3 py-2 text-sm bg-n-background dark:bg-n-slate-1 focus:outline-none focus:ring-1 focus:ring-n-violet-7"
          />
        </div>

        <!-- Context hint -->
        <div class="text-[11px] text-n-slate-11 bg-n-slate-2 dark:bg-n-slate-3 rounded px-3 py-2">
          <template v-if="effectiveDraftId">
            {{
              $t('FEEDBACK_MODAL.CONTEXT.DRAFT', { draftId: effectiveDraftId })
            }}
            ·
            {{
              $t('FEEDBACK_MODAL.CONTEXT.CONVERSATION', {
                conversationId: conversationId,
              })
            }}
          </template>
          <template v-else>
            {{
              $t('FEEDBACK_MODAL.CONTEXT.CONVERSATION', {
                conversationId: conversationId,
              })
            }}
          </template>
        </div>
      </div>

      <!-- Footer -->
      <footer
        class="flex justify-end gap-2 px-5 py-3 border-t border-n-slate-4 bg-n-slate-1/50"
      >
        <button
          type="button"
          class="text-sm px-3 py-1.5 rounded-md text-n-slate-11 hover:bg-n-slate-3"
          :disabled="submitting"
          @click="close"
        >
          {{ $t('FEEDBACK_MODAL.CANCEL') }}
        </button>
        <button
          type="button"
          class="text-sm px-3 py-1.5 rounded-md bg-n-violet-9 hover:bg-n-violet-10 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canSubmit || submitting"
          @click="submit"
        >
          {{
            submitting
              ? $t('FEEDBACK_MODAL.SUBMITTING')
              : $t('FEEDBACK_MODAL.SUBMIT')
          }}
        </button>
      </footer>
    </div>
  </div>
</template>
